<template>
  <div class="device">
    <div v-if="Number.isInteger(deviceScreenId)">
      <screen :idx="String(deviceScreenId)" />
    </div>
    <div v-else class="welcome">
      <div class="title is-1">{{ $t("Welcome in device") }}</div>
      <div v-if="pairingCode">
        <div>{{ $t("Code for pairing device: ") }}</div>
        <div class="pairing-code">{{ pairingCode }}</div>
      </div>
      <div v-else>{{ $t("Loading...") }}</div>
      <div class="has-text-danger" v-if="error">{{ $t(error) }}</div>
    </div>
  </div>
</template>

<script>
import { state, actions } from "@/store"
import screen from "@/components/screen"
import deviceAndroid from "./device-android"
import deviceBrowser from "./device-browser"
import deviceCommon from "./device-common"

const device = {
  ...deviceCommon,
  ...(/android/i.test(navigator.userAgent) ? deviceAndroid : deviceBrowser)
}

export default {
  name: "device-home",
  components: { screen },
  async mounted() {
    actions.logout()
    await device.initDevice()
    await this.bootstrapFailover()
  },
  data() {
    return {
      pairingCode: null,
      error: ""
    }
  },
  computed: {
    device() {
      if (!state || !state.project || !state.project.devices) {
        return null
      }
      return state.project.devices[device.getDeviceId()]
    },
    deviceScreenId() {
      if (this.device == null) {
        return null
      } else if (this.device.screenId == null) {
        return -1
      } else {
        return this.device.screenId
      }
    }
  },
  methods: {
    async bootstrapFailover() {
      try {
        // eslint-disable-next-line no-console
        console.log("Running bootstrap")
        await this.bootstrap()
      } catch (e) {
        this.addError("read-device-data-error", e)
        setTimeout(() => {
          this.bootstrapFailover()
        }, 30000)
      }
    },

    bootstrap() {
      this.error = ""
      let credentials = device.loadCredentials()
      return actions
        .loginWithCredentials(credentials)
        .then(() => {
          this.loadProject(credentials.projectId)
        })
        .catch(e => {
          switch (e.name) {
            case "offline":
              return this.loadOffline()
            case "not-paired":
              return this.generateCodeAndDoPairing(credentials)
            default:
              throw e
          }
        })
    },

    loadOffline() {
      actions.loadProjectDataFromCache()
      setTimeout(this.bootstrapFailover, 30000)
    },

    generateCodeAndDoPairing(credentials) {
      credentials.code = this.setupNewCode()
      return this.doPairing(credentials).then(() => this.bootstrapFailover())
    },

    setupNewCode() {
      this.pairingCode = device.generateCode()
      return this.pairingCode
    },
    async doPairing({ deviceId, code }) {
      await actions.waitForPairing(code) // serves as a trigger for calling pairDevice
      const projectId = await actions.pairDevice(deviceId, code) // TODO: cloud fn should also check projectId
      device.saveCode(code)
      device.saveProjectId(projectId)
      return {
        projectId,
        deviceId,
        code
      }
    },
    loadProject(projectId) {
      actions.terminateProjectListeners()
      return actions.initProjectListeners(
        projectId,
        this.loadProjectErrorHandler
      )
    },
    loadProjectErrorHandler(e) {
      this.addError("read-project-error", e)
      this.pairingCode = null
      this.bootstrapFailover()
    },
    addError(message, e) {
      this.$logger.error(message, e)
      this.error = message
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/scss/style.scss";

html {
  overflow-y: auto;
}

.device {
  .welcome {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    background-color: black;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    color: $device-text-color;
    .title {
      color: $device-text-color;
    }
    .pairing-code {
      font-family: monospace;
      font-size: 4em;
      color: $device-code-color;
    }
  }
}
</style>
